import {dateFromStr, formatDate} from "../../common/time/TimeCalculator";
import {Base} from "../../common/Base";

/**
 * A processor for layer timeseries
 * @abstract
 * @extends Base
 */
class AbstractTimeseriesProcessor extends Base {

    /**
     * @param data {object} a map from string (date) to object of classification/range (string) to count (integer)
     * @param chartStyler {ChartStyler}
     * @param timeCalculator {TimeCalculator}
     */
    constructor(data, chartStyler, timeCalculator) {
        super();
        this.data = data;
        this.chartStyler = chartStyler;
        this.timeCalculator = timeCalculator;
    }

    getClassName() {
        return "AbstractTimeseriesProcessor";
    }

    /**
     * Get data filtered
     * @param from {Date | undefined} filter from
     * @param until {Date | undefined} filter until
     * @return {Array<object>} Array<{x:Array<string>, y:Array<object>, colors:object}> \
     *     - x is Array of dates formatted as YYYY-MM-DD
     *     - y is Array of label (string) to percentage (number)
     *     - colors is a map of label (string) to CSS color (string)
     *
     */
    getData(from, until) {
        const filteredData = this._getFilteredData(from, until);
        const valueSeries = [], colors = {}, ordinals = {};
        const noDataLabel = this.chartStyler.getLabelFunction()(null);
        filteredData.values.forEach(v => {
            const sum = v
                ? Object.keys(v)
                    .map(k => v[k])
                    .reduce((v1, v2) => v1 + v2, 0)
                : null;
            const values = {};
            if (v)
                Object.keys(v).forEach(k => {
                    const value = this.getClassToValue(k);
                    const label = this.chartStyler.getLabelFunction()(value);
                    values[label] = v[k];
                    colors[label] = this.chartStyler.getColorFunction()(value);
                    ordinals[label] = this.getClassToOrdinal(k);
                });
            if (sum !== null && noDataLabel)
                values[noDataLabel] = 100 - sum;
            valueSeries.push(values);
        });
        if (noDataLabel) {
            colors[noDataLabel] = this.chartStyler.getColorFunction()(null);
            ordinals[noDataLabel] = Math.max.apply(null, this.getOrdinals()) + 1;
        }
        return [{x: filteredData.dates, y: valueSeries, colors: colors, ordinals: ordinals}];
    }

    _getFilteredData(from, until) {
        const dates = this.timeCalculator.getTimeSeries(from,until), values = [];
        dates.map(d=>formatDate(d)).forEach(d => {
            values.push(this.data[d] ? this.data[d] : null);
        });
        return {dates, values}
    }

    getClassToValue() {
    }

    getClassToOrdinal() {
    }

    getOrdinals() {
    }
}

class ExtendedReportValuesTimeseriesProcessor extends AbstractTimeseriesProcessor {

    constructor(data, timeCalculator) {
        super(data, undefined, timeCalculator);
    }

    getClassName(){
        return "ExtendedReportValuesTimeseriesProcessor";
    }

    /**
     * Get data filtered
     * @param from {Date | undefined} filter from
     * @param until {Date | undefined} filter until
     * @return {Array<object>} Array<{x:Array<string>, y:Array<number>}> \
     *     - x is Array of dates formatted as YYYY-MM-DD
     *     - y is Array of values (numbers)
     *
     */
    getData(from, until) {
        const filteredData = this._getFilteredData(from, until);
        return [{x: filteredData.dates, y: filteredData.values}];
    }
}

class ExtendedReportValuesAndStatsTimeseriesProcessor extends ExtendedReportValuesTimeseriesProcessor {
    getClassName(){
        return "ExtendedReportValuesAndStatsTimeseriesProcessor";
    }

    /**
     * Get data filtered
     * @param from {Date | undefined} filter from
     * @param until {Date | undefined} filter until
     * @return {Object} (Map of string ("values" and "accumulated") to Array<{x:Array<string>, y:Array<number>, avg:Array<number>, std:Array<number>}>) \
     *     - x is Array of dates formatted as YYYY-MM-DD
     *     - y is Array of values (numbers)
     *
     */
    getData(from, until) {
        const filteredData = this._getFilteredData(from, until);
        const accumulatedValues = [], accumulatedAvg = [], accumulatedVariance = [];
        const variances = filteredData.std.map(std => std * std);
        for (let i = 0; i < filteredData.values.length; i++) {
            accumulatedValues.push(filteredData.values.slice(0, i + 1).reduce((part, a) => part + a, 0));
            accumulatedAvg.push(filteredData.avg.slice(0, i + 1).reduce((part, a) => part + a, 0));
            accumulatedVariance.push(variances.slice(0, i + 1).reduce((part, a) => part + a, 0));
        }
        return {
            values: [
                {x: filteredData.dates, y: filteredData.values},
                {x: filteredData.dates.slice(), y: filteredData.avg},
                {x: filteredData.dates.slice(), y: filteredData.std}
            ],
            accumulated: [
                {x: filteredData.dates.slice(), y: accumulatedValues},
                {x: filteredData.dates.slice(), y: accumulatedAvg},
                {x: filteredData.dates.slice(), y: accumulatedVariance}
            ]
        };
    }

    _getFilteredData(from, until) {
        const dates = this.timeCalculator.getTimeSeries(from,until), values = [], avg = [], std = [];
        dates.map(d=>formatDate(d)).forEach(d => {
            values.push(this.data.values[d] ? this.data.values[d] : null);
            const stat = this.data.stats[d];
            avg.push(stat ? stat.avg : null);
            std.push(stat ? stat.std : null);
        });
        return {dates, values, avg, std}
    }

}

class ExtendedReportRangedTimeseriesProcessor extends AbstractTimeseriesProcessor {
    getClassName(){
        return "ExtendedReportRangedTimeseriesProcessor";
    }

    CLASS_TO_NUMBER = {
        "H3": 2.5,
        "H2": 1.75,
        "H1": 1.25,
        "N": 0,
        "L1": -1.25,
        "L2": -1.75,
        "L3": -2.5
    }
    CLASS_TO_ORDINAL = {
        "H3": 7,
        "H2": 6,
        "H1": 5,
        "N": 4,
        "L3": 1,
        "L2": 2,
        "L1": 3
    }

    getClassToValue(clazz) {
        return this.CLASS_TO_NUMBER[clazz];
    }

    getClassToOrdinal(clazz) {
        return this.CLASS_TO_ORDINAL[clazz];
    }

    getOrdinals() {
        return Object.values(this.CLASS_TO_ORDINAL);
    }
}

class ExtendedReportClassifiedTimeseriesProcessor extends AbstractTimeseriesProcessor {

    getClassName(){
        return "ExtendedReportClassifiedTimeseriesProcessor";
    }

    CLASS_TO_ORDINAL = {
        "0": 4,
        "1": 3,
        "2": 2,
        "3": 1,
    }

    getClassToValue(clazz) {
        return clazz;
    }

    getClassToOrdinal(clazz) {
        return this.CLASS_TO_ORDINAL[clazz];
    }

    getOrdinals() {
        return Object.values(this.CLASS_TO_ORDINAL);
    }
}

export {
    ExtendedReportRangedTimeseriesProcessor,
    ExtendedReportClassifiedTimeseriesProcessor,
    ExtendedReportValuesTimeseriesProcessor,
    ExtendedReportValuesAndStatsTimeseriesProcessor
}